import React, { useEffect, useState, useCallback } from 'react';
import { 
  Container, Paper, Typography, Box, LinearProgress, Button, 
  Divider, Dialog, DialogTitle, DialogContent, List, ListItem, 
  ListItemText, FormControl, InputLabel, Select, MenuItem, CircularProgress
} from '@mui/material';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axiosConfig';

const UserDashboard = () => {
  // 초기 데이터 구조를 명시하여 data가 null일 때 발생하는 렌더링 에러 방지
  const [data, setData] = useState({
    userName: '',
    totalStudyCount: 0,
    overallAverageScore: 0,
    scoreTrends: [],
    recentHistories: [],
    categoryStats: []
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const [open, setOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filter, setFilter] = useState({ category: '', sort: 'LATEST' });

  const THEME_NAVY = '#1a233a';
  const SUCCESS_GREEN = '#4caf50';

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      // 백엔드(DashboardController)에서 Long userPk = 1L 부분을 
      // 현재 로그인한 세션 유저의 PK를 가져오도록 수정해야 student1의 이름이 나옵니다.
      const res = await axios.get('/api/dashboard/user');
      if (res.data) {
        setData(res.data);
      }
    } catch (err) {
      console.error("통계 조회 실패:", err);
      // 에러 발생 시 초기 상태 유지하여 "데이터를 불러올 수 없습니다" 문구 방지
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFullHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = {
        ...filter,
        category: filter.category === 'ALL' ? '' : filter.category
      };
      const res = await axios.get('/api/dashboard/history', { params });
      setHistoryList(res.data || []);
    } catch (err) {
      console.error("이력 조회 실패:", err);
      setHistoryList([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (open) {
      fetchFullHistory();
    }
  }, [open, fetchFullHistory]);

  if (loading) return <Box sx={{ width: '100%', mt: 4 }}><LinearProgress /></Box>;
  
  // 데이터가 아예 없는 초기 사용자도 레이아웃을 볼 수 있도록 !data 체크 로직 제거
  // 대신 data 내부 값들의 기본값(0, [])을 활용합니다.

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pb: 8 }}>
      <Container maxWidth="lg" disableGutters sx={{ pt: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
        
        {/* 1. 상단 헤더 섹션 */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 3, mb: 6, pb: 3, borderBottom: '2px solid #f8fafc' }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 900, color: THEME_NAVY, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-1px' }}>
              {data.userName || '회원'}님의 학습 리포트
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', opacity: 0.8, mt: 1, fontWeight: 400 }}>
              오늘도 수어와 함께 한 걸음 더 성장해볼까요?
            </Typography>
          </Box>
          <Button variant="contained" size="large" startIcon={<PlayCircleFilledIcon />} onClick={() => navigate('/category-select')}
            sx={{ bgcolor: SUCCESS_GREEN, color: 'white', px: 4, py: 2, width: { xs: '100%', sm: 'auto' }, borderRadius: '16px', fontWeight: 800, fontSize: '1.1rem', boxShadow: '0 8px 20px rgba(76, 175, 80, 0.2)', '&:hover': { bgcolor: '#43a047' } }}>
            수어 학습하러 가기
          </Button>
        </Box>

        {/* 2. 요약 카드 그리드 */}
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, mb: 3 }}>
          <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', textAlign: 'center', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={600}>총 학습 횟수</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: THEME_NAVY, mt: 1 }}>{data.totalStudyCount || 0}회</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', textAlign: 'center', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={600}>전체 평균 점수</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#2e7d32', mt: 1 }}>{data.overallAverageScore || 0}점</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', textAlign: 'center', bgcolor: THEME_NAVY, color: 'white' }}>
            <Typography variant="subtitle1" sx={{ opacity: 0.8 }} fontWeight={600}>오늘의 한마디</Typography>
            <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>꾸준함이 실력이 됩니다!</Typography>
          </Paper>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, width: '100%' }}>
          {/* 3. 최근 성적 추이 차트 */}
          <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #e2e8f0', flex: 1, width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 4, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon sx={{ color: THEME_NAVY }} /> 성적 변동 추이
            </Typography>
            <Box sx={{ width: '100%', height: 350 }}>
              {data.scoreTrends && data.scoreTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.scoreTrends}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dy={10} />
                    <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                    <Line type="monotone" dataKey="score" stroke={THEME_NAVY} strokeWidth={4} dot={{ r: 6, fill: THEME_NAVY, strokeWidth: 3, stroke: '#fff' }} activeDot={{ r: 8 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>차트 데이터가 없습니다.</Box>
              )}
            </Box>
          </Paper>

          {/* 4. 최근 학습 기록 */}
          <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #e2e8f0', flex: 1, width: '100%', position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon sx={{ color: THEME_NAVY }} /> 최근 학습 기록
              </Typography>
              <Button size="small" onClick={() => setOpen(true)} sx={{ fontWeight: 700, color: THEME_NAVY }}>더보기 +</Button>
            </Box>
            <List disablePadding>
              {data.recentHistories && data.recentHistories.length > 0 ? (
                data.recentHistories.slice(0, 5).map((history, idx) => (
                  <ListItem key={idx} sx={{ px: 0, py: 1.5, borderBottom: idx === (data.recentHistories.length - 1) || idx === 4 ? 'none' : '1px solid #f1f5f9' }}>
                    <ListItemText 
                      primary={<Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography fontWeight={700}>{history.categoryName}</Typography><Typography fontWeight={800} color={SUCCESS_GREEN}>{history.score}점</Typography></Box>}
                      secondary={`${history.date} | ${history.duration || ''}`}
                    />
                  </ListItem>
                ))
              ) : (
                <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>기록이 없습니다.</Typography>
              )}
            </List>
          </Paper>
        </Box>

        {/* 5. 상세 학습 기록 팝업 (Dialog) */}
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px' } }}>
          <DialogTitle sx={{ fontWeight: 800, p: 3, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            전체 학습 이력 조회
            <Button onClick={() => setOpen(false)} color="inherit">닫기</Button>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 2.5, mb: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="category-select-label">카테고리</InputLabel>
                <Select labelId="category-select-label" id="category-select" value={filter.category} label="카테고리"
                  onChange={(e) => setFilter({...filter, category: e.target.value})}>
                  <MenuItem value="">전체 카테고리</MenuItem> 
                  {data.categoryStats && data.categoryStats.map(cat => (
                    <MenuItem key={cat.categoryName} value={cat.categoryName}>{cat.categoryName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth size="small">
                <InputLabel id="sort-select-label">정렬</InputLabel>
                <Select labelId="sort-select-label" id="sort-select" value={filter.sort} label="정렬"
                  onChange={(e) => setFilter({...filter, sort: e.target.value})}>
                  <MenuItem value="LATEST">최신순</MenuItem>
                  <MenuItem value="SCORE_DESC">점수 높은순</MenuItem>
                  <MenuItem value="SCORE_ASC">점수 낮은순</MenuItem>
                </Select>
              </FormControl>

              <Button variant="contained" onClick={fetchFullHistory} sx={{ bgcolor: THEME_NAVY, minWidth: { xs: '100%', sm: '60px' }, height: '40px' }}>
                <SearchIcon />
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {historyLoading ? (
              <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={30} sx={{ color: THEME_NAVY }} /></Box>
            ) : (
              <List sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                {historyList.map((h, i) => (
                  <ListItem key={i} sx={{ mb: 1, border: '1px solid #f1f5f9', borderRadius: '12px' }}>
                    <ListItemText 
                      primary={<Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography fontWeight={700}>{h.categoryName}</Typography><Typography fontWeight={800} color={SUCCESS_GREEN}>{h.score}점</Typography></Box>}
                      secondary={`${h.date} | 소요시간: ${h.duration || ''}`}
                    />
                  </ListItem>
                ))}
                {historyList.length === 0 && <Typography align="center" color="text.secondary" sx={{ py: 4 }}>조회된 데이터가 없습니다.</Typography>}
              </List>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default UserDashboard;